import React, { Component } from 'react';
import { NavLink } from 'react-router-dom';

import Section from '../components/Section';
import Loading from '../components/Loading';
import Histogram from './Histogram';
import { HistogramLabel } from './Histogram';

import api from '../util/api';
import utils from '../util/utils';

const stats = [
    { key: 'summary', api: 'countstats', name: 'Summary', percent: false, desc: <>Shows overall number of characters in the <i>Data for Azeroth</i> database. Though I try to proactively find characters, the data doesn't include all characters to ever exist. Excluded characters mean they have account-wide achievements disabled and don't count in rankings and rarity calculations.</> },
    { key: 'regions', api: 'realmstats', name: 'Regions', percent: false, desc: 'Shows a breakdown of characters by region.' },
    { key: 'realms', api: 'realmstats', name: 'Realms', percent: false, desc: 'Shows a breakdown of characters by realm.' },
    { key: 'factions', api: 'factionstats', name: 'Factions', percent: true, maxlevel: true, sort: true, desc: 'Shows a breakdown of characters by faction.' },
    { key: 'classes', api: 'classstats', name: 'Classes', percent: true, maxlevel: true, sort: true, desc: 'Shows a breakdown of characters by class.' },
    { key: 'specs', api: 'specstats', name: 'Specs', percent: true, maxlevel: true, sort: true, desc: 'Shows a breakdown of characters by class specialization.' },
    { key: 'races', api: 'racestats', name: 'Races', percent: true, maxlevel: true, sort: true, desc: 'Shows a breakdown of characters by race.' },
    { key: 'classes-races', api: 'classracestats', name: 'Class+Race', percent: true, maxlevel: true, sort: true, desc: 'Shows a breakdown of characters by class and race combination.' },
    { key: 'genders', api: 'genderstats', name: 'Genders', percent: true, maxlevel: true, sort: true, desc: 'Shows a breakdown of characters by gender.' },
    { key: 'guild-status', api: 'countstats', name: 'Guild Status', percent: true, desc: 'Shows a breakdown of characters and accounts by whether they\'re in a guild.' },
    { key: 'levels', api: 'levelstats', name: 'Levels', percent: true, desc: 'Shows a breakdown of characters by level.' },
    { key: 'ilvls', api: 'ilvlstats', name: 'Item Levels', percent: true, maxlevel: true, desc: 'Shows a breakdown of characters by item level.' },
    { key: 'updates', api: 'refreshstats', name: 'Last Updated', percent: true, desc: <>Shows a breakdown of characters according to when they were last updated on <i>Data for Azeroth</i>.</> },
    { key: 'logins', api: 'loginstats', name: 'Last Activity', percent: true, desc: 'Shows a breakdown of accounts according to when they were last active in game. This is calculated relative to the last updated date, to normalize the data.' },
    { key: 'ages', api: 'agestats', name: 'Account Age', percent: true, desc: 'Shows a breakdown of accounts according to their age, estimated using achievements.' },
    { key: 'alts', api: 'altstats', name: '# Alts', percent: true, desc: 'Shows a breakdown of accounts according to how many alts they have.' },

    { key: 'collectors-bounty', api: 'achievementstats', name: `Collector's Bounty`, percent: false, desc: <>Shows the number of accounts that earned the following achievements and mounts during the Collector's Bounty event. Previous months are shown for reference. Achievement timestamps are provided by Blizzard and should be accurate. However, mounts are estimated based on when <i>Data for Azeroth</i> refreshed the characters. All statistics are based on the <i>Data for Azeroth</i> database and are not a reflection of the overall WoW population.</> }
];

const CustomLabel = (props) => {
    const header = props.data?.[props.index]?.header;
    if (!header) return <HistogramLabel {...props} />; // standard label

    const leggo = new Set([ 428, 429, 426, 725 ]);
    const id = props.data?.[props.index]?.id;

    // replace the row with a header instead
    return (
        <g transform={`translate(${props.x},${props.y})`}>
            <rect x={0} y={0} width="100%" height={props.height} fill="#292d32" />
            <text x={5} y={props.height/2} dy="5" fill={leggo.has(id) ? '#ff8000' : '#f0ebe0'} textAnchor="start" fontSize="1.1em">{header}</text>
        </g>
    );
};

class Statistics extends Component {
    constructor(props) {
        super(props);
        this.state = {
            error: false
        };
    }

    componentDidMount() {
        this.lookup();
    }
    
    componentDidUpdate(previous) {
        if (previous.category === this.props.category) return;
        this.lookup();
    }

    async lookup() {
        if (this.state[this.props.category]) return; // already loaded

        this.setState({ error: false });
        switch (this.props.category) {
            case 'summary':
                let summary = await api.web('countstats');
                summary = [
                    { bucket: 'All Characters', count: summary[0].characters },
                    { bucket: 'Included Characters', count: summary[0].charactersincluded },
                    { bucket: 'Excluded Characters', count: summary[0].charactersexcluded },
                    { bucket: 'Accounts', count: summary[0].accounts }
                ];
                return this.setState({ summary });
            case 'collectors-bounty':
                {
                    const order = [
                        428, 429, 426, 725, // legendaries
                        9713, // mount collecting
                        424, 729, // classic mounts
                        882, 883, 884, 885, // tbc mounts
                        2081, 4626, 4625 // wotlk mounts
                    ];
                    const orderOf = id => (order.indexOf(id) + 1) || (10000 + id);
                    const months = {
                        '2025-07': 1,
                        '2025-06': 2,
                        '2025-05': 3,
                        '2025-04': 4
                    };
                    const mountsWithAchievement = new Set([
                        69, 118, // classic
                        213, 183, 185, 168, // tbc
                        287, 286, 363, 304 // wotlk
                    ]);

                    // filter achievements to only include 2 months
                    let json = await api.web('achievements');
                    let achievements = await api.web('achievementstats');
                    achievements = achievements.filter(row => months[row.bucket.split('#')[1]]);
                    achievements.forEach(row => row.name = json.achievements.find(a => a.id === parseInt(row.bucket.split('#')[0]))?.name);

                    // filter mounts to exclude the ones that have achievements
                    json = await api.web('mounts');
                    let mounts = await api.web('mountstats');
                    mounts = mounts.filter(row => !mountsWithAchievement.has(parseInt(row.bucket.split('#')[0])));
                    mounts.forEach(row => row.name = json.mounts.find(a => a.id === parseInt(row.bucket.split('#')[0]))?.name + ' (Estimated)');

                    // this relies on IDs not overlapping between the two sets
                    let combined = [ ...achievements, ...mounts ];
                    combined.forEach(row => {
                        const bucket = row.bucket.split('#');
                        row.id = parseInt(bucket[0]);
                        row.date = bucket[1];
                    });
                    combined.sort((a, b) => {
                        if (a.id === b.id) {
                            // same id, so sort by date
                            return months[a.date] - months[b.date];
                        } else {
                            // sort by id
                            return orderOf(a.id) - orderOf(b.id);
                        }
                    });

                    // copy array but add headers where needed
                    let lastId = -1;
                    let copy = [];
                    for (let row of combined) {
                        if (row.id !== lastId) {
                            lastId = row.id;
                            copy.push({ bucket: '', header: row.name, count: 0, id: row.id });
                        }

                        let bucket = new Date(row.date + '-02').toLocaleString(undefined, { month: 'long' });
                        copy.push({ bucket, count: row.count, fill: row.date === '2025-07' ? '#375a7f' : '#495a6d' });
                    }

                    return this.setState({ 'collectors-bounty': copy });
                }
            case 'guild-status':
                let g = await api.web('countstats');
                const csum = g[0].charactersguilded + g[0].charactersguildless;
                const asum = g[0].accountsguilded + g[0].accountsguildless;
                g = [
                    { bucket: 'Guilded Characters', count: g[0].charactersguilded, percent: 100.0 * g[0].charactersguilded / csum },
                    { bucket: 'Guildless Characters', count: g[0].charactersguildless, percent: 100.0 * g[0].charactersguildless / csum },
                    { bucket: 'Guilded Accounts', count: g[0].accountsguilded, percent: 100.0 * g[0].accountsguilded / asum },
                    { bucket: 'Guildless Accounts', count: g[0].accountsguildless, percent: 100.0 * g[0].accountsguildless / asum }
                ];
                return this.setState({ 'guild-status': g });
            case 'regions':
                let regions = await api.web('realmstats');
                regions = regions.map(row => {
                    const bucket = row.bucket.split('#')[0].toUpperCase();
                    return { bucket, count: row.count };
                });
                regions = this.mergebuckets(regions);
                regions.sort((a, b) => b.count - a.count);
                return this.setState({ regions });
            case 'realms':
                const json = await api.web('realms');

                let realms = await api.web('realmstats');
                realms = realms.map(row => {
                    const split = row.bucket.split('#');
                    const lookup = json.realms.find(l => (l.region === split[0]) && (l.slug === split[1]));
                    return { bucket: lookup?.name || row.bucket, count: row.count, region: split[0] };
                });
                return this.setState({ realms });
            case 'classes':
                let classes = await api.web('classstats');
                classes = classes.map(row => {
                    const lookup = utils.classes.find(l => l.key === row.bucket);
                    return { bucket: lookup?.value || row.bucket, count: row.count, fill: lookup?.color, maxlevel: row.maxlevel };
                });
                return this.setState({ classes });
            case 'specs':
                let specs = await api.web('specstats');
                specs = specs.filter(row => row.bucket).map(row => {
                    const lookup = utils.specs.find(l => l.key === row.bucket);
                    const entry = { bucket: lookup?.value || row.bucket || 'Unknown', count: row.count, maxlevel: row.maxlevel };
                    const clazz = utils.classes.find(l => l.key === lookup?.class);
                    if (clazz?.color) entry.fill = clazz.color;
                    return entry;
                });
                return this.setState({ specs });
            case 'factions':
                let factions = await api.web('factionstats');
                factions = factions.map(row => {
                    const lookup = utils.factions.find(l => l.key === row.bucket);
                    return { bucket: lookup?.value || row.bucket, count: row.count, fill: lookup?.color, maxlevel: row.maxlevel };
                });
                return this.setState({ factions });
            case 'races':
                let races = await api.web('racestats');
                races = races.map(row => {
                    const lookup = utils.races.find(l => l.key === row.bucket);
                    return { bucket: lookup?.value || row.bucket, count: row.count, fill: lookup?.color, maxlevel: row.maxlevel };
                });
                return this.setState({ races });
            case 'classes-races':
                let cr = await api.web('classracestats');
                cr = cr.map(row => {
                    const parts = row.bucket?.split('#');
                    const clazz = utils.classes.find(l => l.key === parseInt(parts?.[0]));
                    const race = utils.races.find(l => l.key === parseInt(parts?.[1]));
                    const bucket = clazz && race ? race?.value + '\n' + clazz?.value : row.bucket;
                    return { bucket, count: row.count, fill: clazz?.color, maxlevel: row.maxlevel };
                });
                return this.setState({ 'classes-races': cr });
            case 'genders':
                let genders = await api.web('genderstats');
                genders = genders.map(row => {
                    const lookup = { 0: 'Male', 1: 'Female' };
                    return { bucket: lookup[row.bucket] || row.bucket, count: row.count, maxlevel: row.maxlevel };
                });
                return this.setState({ genders });
            case 'levels':
                let levels = await api.web('levelstats');
                levels = levels.map(row => {
                    const lookup = { 0: '1 - 9', 1: '10 - 19', 2: '20 - 29', 3: '30 - 39', 4: '40 - 49', 5: '50 - 59', 6: '60 - 69', 7: '70 - 79', 8: 'Level 80' };
                    return { bucket: lookup[~~(row.bucket / 10)], count: row.count };
                });
                levels = this.mergebuckets(levels);
                return this.setState({ levels });
            case 'ilvls':
                let ilvls = await api.web('ilvlstats');
                ilvls = ilvls.map(row => {
                    const max = row.bucket + 9;
                    return { bucket: row.bucket + ' - ' + max, count: row.count, maxlevel: row.maxlevel };
                });
                return this.setState({ ilvls });
            case 'alts':
                let alts = await api.web('altstats');
                alts = alts.map(row => {
                    const bucket = row.bucket + ' Character' + (row.bucket > 1 ? 's' : '');
                    return { bucket: row.bucket > 20 ? 'More' : bucket, count: row.count };
                });
                alts = this.mergebuckets(alts);
                return this.setState({ alts });
            case 'updates':
                let updates = await api.web('refreshstats');
                updates = updates.map(row => {
                    const bucket = (row.bucket === 0 ? 'Today' : row.bucket + ' day') + (row.bucket > 1 ? 's' : '') + (row.bucket > 0 ? ' ago' : '');
                    return { bucket, count: row.count };
                });
                return this.setState({ updates });
            case 'logins':
                let logins = await api.web('loginstats');
                logins = logins.map(row => {
                    const week = ~~(row.bucket / 7);
                    return { bucket: week, count: row.count };
                });
                const cutoff = logins.find(row => row.count < 10);
                logins = logins.map(row => {
                    const bucket = (row.bucket || 'This') + ' week' + (row.bucket > 1 ? 's' : '') + (row.bucket > 0 ? ' ago' : '');
                    return { bucket: row.bucket >= cutoff.bucket ? 'Older' : bucket, count: row.count };
                });
                logins = this.mergebuckets(logins);
                return this.setState({ logins });
            case 'ages':
                let ages = await api.web('agestats');
                ages = ages.map(row => {
                    const bucket = row.bucket + '+ years';
                    return { bucket, count: row.count };
                });
                return this.setState({ ages });
            default:
                return this.setState({ error: true });
        }
    }

    mergebuckets(array) {
        const result = [];

        for (let row of array) {
            const previous = result.find(e => e.bucket === row.bucket);
            if (previous) {
                previous.count += row.count;
            } else {
                result.push({ ...row });
            }
        }

        return result;
    }

    render() {
        document.title = 'Statistics | Data for Azeroth | World of Warcraft Leaderboards for Collectors';

        const stat = stats.find(s => s.key === this.props.category);
        let data = this.state[this.props.category];
        let subtitle = stat.name;

        const tabs = [];
        stats.forEach(stat => {
            tabs.push(<NavLink to={'/stats/' + stat.key} className="btn btn-primary" key={stat.key}>{stat.name}</NavLink>);
        });

        const subtabs = [];
        if (stat.key === 'realms') {
            const selected = this.props.subcategory?.toLowerCase();
            data = data?.filter(row => row.region === selected);
            subtitle = (this.props.subcategory?.toUpperCase() || '') + ' ' + subtitle;
            [ 'eu', 'kr', 'tw', 'us' ].forEach(region => {
                subtabs.push(<NavLink to={'/stats/' + stat.key + '/' + region} className="btn btn-primary" key={region}>{region.toUpperCase()}</NavLink>);
            });
        }
        if (stat.maxlevel) {
            if (this.props.subcategory?.toLowerCase() === 'maxlevel') {
                data = data?.map(row => ({ ...row, count: row.maxlevel }));
                if (stat.sort) data?.sort((a, b) => b.count - a.count); // need to re-sort the data
            }
            subtabs.push(<NavLink to={'/stats/' + stat.key + '/'} exact className="btn btn-primary" key="all">All Levels</NavLink>);
            subtabs.push(<NavLink to={'/stats/' + stat.key + '/maxlevel'} className="btn btn-primary" key="maxlevel">Max Level Only</NavLink>);
        }

        const disclaimer = <>All statistics are computed across the full <i>Data for Azeroth</i> database, which may not be an accurate reflection of the overall WoW population.</>;

        return (
            <Section title="Statistics" subtitle={subtitle} intro={stat.desc} footer={disclaimer}>
                <div className="mb-3">
                    <div className={'btn-group' + (window.bootstrap.md.matches ? ' flex-wrap' : '-vertical w-100')}>
                        {tabs}
                    </div>
                </div>
                {subtabs.length ?
                    <div className="mb-3">
                        <div className={'btn-group' + (window.bootstrap.md.matches ? ' flex-wrap' : '-vertical w-100')}>
                            {subtabs}
                        </div>
                    </div>
                : null}
                <div id="statistics" className="small mt-3" style={{ height: 40 + 40 * (data?.length || 1) }}>
                    {this.state.error ? <div>Unrecognized statistic. Choose from a tab above.</div> : null}
                    {data ? <Histogram data={data} percent={stat.percent} label={CustomLabel} /> : <Loading message="Loading..." />}
                </div>
            </Section>
        );
    }
}

export default Statistics;